// UserSettingsStyles.js
import { alpha } from '@mui/material';
import heroImage1 from '../assets/heroimage1.png';

const theme = {
  colors: {
    white: '#FFFFFF',
    black: '#000000',
    primary: '#F89622',
    secondary: '#66202D',
    text: '#1E1E1E',
    gray: '#4A4A4A',
    border: '#ECECEC',
    disabled: '#AEB4AC',
  },
  fonts: {
    primary: "'PP Neue Montreal', 'Inter', sans-serif",
    secondary: "'Lato', sans-serif",
  },
  spacing: {
    xs: '8px',
    sm: '16px',
    md: '24px',
    lg: '32px',
    xl: '40px',
  },
  borderRadius: {
    small: '6px',
    medium: '16px',
    large: '138px',
    pill: '390px',
  },
};

export const UserSettingsStyles = {
  container: {
    fontFamily: theme.fonts.primary,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    background: theme.colors.white,
  },
  appBar: (heroImage) => ({
    backgroundImage: `url(${heroImage1})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    boxShadow: 'none',
    padding: '10px 117px',
    height: '260px',
    zIndex: 1,
  }),
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '14px',
  },
  logo: {
    width: '118px',
    height: '67px',
    objectFit: 'contain',
  },
  navButtonsContainer: {
    display: 'flex',
    gap: '20px',
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
  },
  navButton: {
    color: theme.colors.white,
    textTransform: 'none',
    fontSize: '18px',
    fontWeight: 400,
    lineHeight: '200%',
    padding: '8px 16px',
    '&:hover': {
      backgroundColor: alpha(theme.colors.white, 0.1),
    },
  },
  logoutButton: {
    color: theme.colors.white,
    textTransform: 'uppercase',
    fontSize: '16px',
    fontWeight: 700,
    fontFamily: theme.fonts.secondary,
    lineHeight: '200%',
    padding: '8px 16px',
    '&:hover': {
      backgroundColor: alpha(theme.colors.white, 0.1),
    },
  },
  heroContent: {
    height: 'calc(260px - 64px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.colors.white,
    textAlign: 'center',
    marginBottom: '60px',
  },
  heroTitle: {
    fontWeight: 500,
    fontSize: '48px',
    lineHeight: '90%',
    fontFamily: theme.fonts.primary,
    textTransform: 'none',
  },
  contentContainer: {
    flex: 1,
    padding: '0px 0px',
    justifyContent: 'center',
    marginTop: '-70px',
    zIndex: 2,
  },
  mainContentBox: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.medium,
    border: `1px solid ${theme.colors.border}`,
    boxShadow: '0px 14px 24px rgba(0, 0, 0, 0.05)',
    padding: '30px',
    width: '1328px',
    margin: '0 auto',
    minHeight: '680px', // Increased to accommodate larger form boxes (600px height + 30px top padding + 30px bottom padding + 20px for spacing)
  },
  formBox: {
    border: `1px solid ${theme.colors.disabled}`,
    borderRadius: theme.borderRadius.small,
    width: '613px',
    height: '650px', // Increased from 545px to fit 6 fields and button (559px required, added extra for breathing room)
    padding: '40px',
    boxSizing: 'border-box',
  },
  formTitle: {
    marginBottom: '15px',
    fontFamily: theme.fonts.secondary,
    fontWeight: 800,
    fontSize: '26px',
    lineHeight: '200%',
    color: theme.colors.text,
    textAlign: 'center',
  },
  textField: {
    marginBottom: '20px',
    backgroundColor: '#FAFAFA',
    borderRadius: theme.borderRadius.large,
    width: '348px',
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
    '& .MuiOutlinedInput-root': {
      border: `1px solid ${theme.colors.disabled}`,
      borderRadius: theme.borderRadius.large,
      '& fieldset': {
        border: 'none',
      },
    },
    '& .MuiInputLabel-root': {
      fontFamily: theme.fonts.primary,
      fontSize: '16px',
      lineHeight: '200%',
      color: theme.colors.gray,
    },
    '& .MuiInputBase-input': {
      fontFamily: theme.fonts.primary,
      fontSize: '16px',
      color: theme.colors.text,
    },
  },
  errorText: {
    marginBottom: '15px',
    textAlign: 'center',
    fontFamily: theme.fonts.primary,
    fontSize: '14px',
  },
  actionButton: {
    backgroundColor: theme.colors.secondary,
    color: theme.colors.white,
    borderRadius: theme.borderRadius.pill,
    textTransform: 'uppercase',
    padding: '10px 30px',
    fontFamily: theme.fonts.primary,
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '130%',
    width: '208px',
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
    '&:hover': {
      backgroundColor: '#551A25',
    },
  },
  footer: {
    padding: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: theme.colors.black,
    width: '1328px',
    margin: '0 auto',
  },
  footerText: {
    fontFamily: theme.fonts.secondary,
    fontSize: '18px',
    lineHeight: '200%',
    color: theme.colors.text,
  },
  footerButton: {
    color: theme.colors.text,
    textTransform: 'none',
    fontFamily: theme.fonts.secondary,
    fontSize: '18px',
    lineHeight: '200%',
    padding: '0 16px',
  },
  dialogTitle: {
    color: theme.colors.primary,
    fontFamily: theme.fonts.primary,
    fontSize: '24px',
  },
  dialogContentText: {
    fontFamily: theme.fonts.primary,
    fontSize: '16px',
  },
  dialogActionButton: {
    color: theme.colors.secondary,
    textTransform: 'none',
    fontFamily: theme.fonts.primary,
    fontSize: '14px',
  },
};

export default UserSettingsStyles;